// Application Catalogue: version arithmetic shared by the server and the page.
//
// Everything here is a pure function over the release-cycle shape that
// endoflife.date returns for a product:
//   [{ cycle: '29', releaseDate, eol: false | 'YYYY-MM-DD', latest: '29.8.0', latestReleaseDate, lts, support? }, ...]
// newest cycle first. The server stores those rows per application and calls
// deriveSupport() when it lists or reads an application; the page calls the
// same function to explain a version someone is about to log. One rule, one
// answer to "is this application past end of life".

export const CRITICALITIES = ['low', 'medium', 'high'];
export const CRITICALITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };

export const HOSTINGS = ['self_hosted', 'saas'];
export const HOSTING_LABELS = { self_hosted: 'Self hosted', saas: 'SaaS (vendor managed)' };

export const VERSION_SOURCES = ['endoflife', 'manual'];
export const VERSION_SOURCE_LABELS = { endoflife: 'endoflife.date', manual: 'Entered by hand' };

export const SUPPORT_STATUSES = ['past_eol', 'ending_soon', 'supported', 'unknown', 'vendor_managed'];
export const SUPPORT_STATUS_LABELS = {
  past_eol: 'Past end of life',
  ending_soon: 'Support ending soon',
  supported: 'Supported',
  unknown: 'Unknown',
  vendor_managed: 'Vendor managed',
};

// The alerts the nightly job sends: three fixed points before security support
// ends, then a weekly reminder for as long as the application stays past it.
export const ALERT_KINDS = ['eol_90', 'eol_30', 'eol_0', 'eol_weekly'];
export const ALERT_DAYS = { eol_90: 90, eol_30: 30, eol_0: 0 };

// How far ahead "ending soon" looks, and when the first alert goes.
export const WARN_DAYS = 90;

// How long a hand-maintained application may go without somebody re-checking
// the vendor before the support owner is reminded.
export const MANUAL_RECHECK_DAYS = 90;

const clean = (v) => String(v ?? '').trim().replace(/^v/i, '');

// Numeric segments of a version string, so 10.18 sorts after 10.9.
export function versionParts(v) {
  const s = clean(v);
  if (!s) return null;
  const parts = s.split(/[^0-9]+/).filter(Boolean).map(Number);
  return parts.length ? parts : null;
}

export function compareVersions(a, b) {
  const pa = versionParts(a) || [];
  const pb = versionParts(b) || [];
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

export function lowestVersion(versions) {
  const present = (versions || []).filter(v => versionParts(v));
  if (!present.length) return null;
  return present.reduce((low, v) => (compareVersions(v, low) < 0 ? v : low));
}

export function majorOf(v) {
  const parts = versionParts(v);
  return parts ? parts[0] : null;
}

export function majorsBehind(installed, latest) {
  const a = majorOf(installed);
  const b = majorOf(latest);
  if (a == null || b == null) return null;
  return Math.max(0, b - a);
}

// The cycle an installed version belongs to: the version equals the cycle name
// or starts with it followed by a dot. Longer cycle names are tried first so
// 26.1.3 lands on 26.1 rather than on a cycle called 26.
export function findInstalledCycle(installed, cycles) {
  const v = clean(installed);
  if (!v || !Array.isArray(cycles) || !cycles.length) return null;
  const sorted = [...cycles].sort((a, b) => String(b.cycle).length - String(a.cycle).length);
  for (const c of sorted) {
    const name = String(c.cycle ?? '');
    if (!name) continue;
    if (v === name || v.startsWith(name + '.')) return c;
  }
  return null;
}

const toUtcDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

// Whole days from `today` to `date` (negative when it has passed).
export function daysBetween(today, date) {
  const a = toUtcDay(today);
  const b = toUtcDay(date);
  if (a == null || b == null) return null;
  return Math.round((b - a) / 86400000);
}

export function todayIso(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

// Where an application stands. `app` carries hosting, version_source, the
// installed version that decides (the lowest across its instances), the
// manual fields, and the cycles the last check stored.
export function deriveSupport(app, today = todayIso(), { warnDays = WARN_DAYS } = {}) {
  const out = {
    status: 'unknown',
    installed_version: app?.installed_version || null,
    installed_cycle: null,
    latest_version: null,
    latest_cycle: null,
    support_ends: null,
    days_to_eol: null,
    majors_behind: null,
  };
  if (app?.hosting === 'saas') return { ...out, status: 'vendor_managed' };

  const installed = clean(app?.installed_version);
  let eolKnownPassed = false;

  if (app?.version_source === 'endoflife') {
    const cycles = Array.isArray(app.cycles) ? app.cycles : [];
    const newest = cycles[0] || null;
    out.latest_version = newest?.latest || null;
    out.latest_cycle = newest ? String(newest.cycle) : null;
    const mine = installed ? findInstalledCycle(installed, cycles) : null;
    if (mine) {
      out.installed_cycle = String(mine.cycle);
      if (typeof mine.eol === 'string') out.support_ends = mine.eol;
      else if (mine.eol === true) eolKnownPassed = true;
    }
  } else {
    out.latest_version = app?.manual_latest_version || null;
    out.support_ends = app?.manual_support_ends || null;
  }

  if (installed && out.latest_version) out.majors_behind = majorsBehind(installed, out.latest_version);

  if (out.support_ends) out.days_to_eol = daysBetween(today, out.support_ends);

  if (!installed) return out;
  if (eolKnownPassed || (out.days_to_eol != null && out.days_to_eol <= 0)) out.status = 'past_eol';
  else if (out.days_to_eol != null && out.days_to_eol <= warnDays) out.status = 'ending_soon';
  else if (out.days_to_eol != null) out.status = 'supported';
  else if (app?.version_source === 'endoflife' && out.installed_cycle) out.status = 'supported';
  else out.status = 'unknown';
  return out;
}
